/**
 * The tax identifiers a customer can carry, and which country each of them belongs to (§10, §4.6).
 *
 * **The type is a wire value, not a word.** `eu_vat`, `ca_pst_bc`, `br_cnpj` — the payment provider
 * accepts that closed set and nothing else, and it answers anything outside it with
 * *"Invalid type: must be one of ad_nrt, ae_trn, …"*, a sentence naming a hundred and eighteen
 * codes and pointing at none of them. The billing page used to ask for it in a text box captioned
 * "Type", so a customer with a German VAT number typed `VAT`, or `vat`, or `DE`, and the save
 * failed at the provider **after** the row had already been written locally: the store then held a
 * tax ID it could never send, the customer had a wall of codes for an error message, and the
 * reverse-charge the number was entered for silently never applied.
 *
 * So the vocabulary lives here and the customer picks from it. Two things make that pickable:
 *
 * - **The country is the filter.** A customer entering a tax ID has exactly one jurisdiction in
 *   mind, and it is almost always the one already in their billing address — so the address's
 *   country brings its own documents to the top of the list, and the other hundred stay reachable
 *   below rather than being hidden. Hidden would be wrong: a company registered somewhere it does
 *   not bill from is ordinary, and a picker that omitted the case would leave it unenterable.
 * - **The country of a type is its prefix**, derived rather than restated, because `de_stn` naming
 *   `DE` in a second column is a fact that can disagree with itself. The exceptions declare
 *   themselves: `eu_vat` and `eu_oss_vat` belong to twenty-seven countries and `ic_nif` is issued
 *   in Spain, and a prefix that is not an ISO-3166 code (a future `xx_`) is simply not tied to a
 *   country rather than inventing one.
 *
 * Names come from `Intl` for the reason `Countries.ts` gives; the labels here are the **document**
 * — "VAT number", "Steuernummer", "PST number (British Columbia)" — never the country, so the two
 * halves are composed once, in the reader's language, instead of a table of English going stale.
 *
 * The examples are specimens for the placeholder, and nothing validates against them: the provider
 * is the authority on the format, and a store refusing a number its provider would have accepted is
 * worse than one that passes it on.
 *
 * `Server/Source/Payments/TaxIDTypes.ts` holds the same set of wire values, and
 * `Client/tests/TaxIDTypeAgreement.test.ts` is what keeps the two from drifting.
 */

import { COUNTRY_CODES, countryName } from './Countries';

export interface TaxIDType {
	/** The provider's wire value, e.g. `eu_vat`. This is what is stored and sent. */
	value: string;
	/** The document's own name, without its country: "VAT number", "Steuernummer". */
	label: string;
	/** Every ISO-3166 alpha-2 code this document is issued in. Empty means "tied to no country". */
	countries: readonly string[];
	/** What a number of this kind looks like. Shown as the placeholder; never validated against. */
	example: string;
	/**
	 * Names the jurisdiction for a document that is not one country's — "European Union". Absent
	 * for the ordinary case, where the country's own name in the reader's language is the answer.
	 */
	region?: string;
}

/**
 * The member states a VAT number registered under the EU scheme can come from.
 *
 * Listed rather than derived: "in the EU" is a political fact with a date on it, and there is no
 * property of an ISO code that says it.
 */
const EU_VAT_COUNTRIES: readonly string[] = [
	'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT',
	'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
];

/**
 * `[wire value, document name, specimen, countries?, region?]`, in wire-value order — which is also
 * the order the provider documents them, so the two can be read side by side when the list next
 * changes. The fourth entry is present only where the prefix does not answer the question, and the
 * one place the order is broken says why on the line above it.
 */
type Row = readonly [string, string, string, (readonly string[])?, string?];

const ROWS: readonly Row[] = [
	['ad_nrt', 'NRT number', 'A-123456-Z'],
	['ae_trn', 'TRN', '123456789012345'],
	['al_tin', 'TIN', 'J12345678N'],
	['am_tin', 'TIN', '02637741'],
	['ao_tin', 'TIN', '5401234567'],
	['ar_cuit', 'CUIT', '12-34567890-1'],
	['au_abn', 'ABN', '12345678912'],
	['au_arn', 'Taxation Office reference number', '123456789123'],
	['aw_tin', 'TIN', '1234567'],
	['az_tin', 'TIN', '1234567890'],
	['ba_tin', 'TIN', '123456789012'],
	['bb_tin', 'TIN', '1123456789012'],
	['bd_bin', 'BIN', '123456789-0123'],
	['bf_ifu', 'IFU', '00000000A'],
	['bg_uic', 'UIC', '123456789'],
	['bh_vat', 'VAT number', '12345678901234'],
	['bj_ifu', 'IFU', '1234567890123'],
	['bo_tin', 'TIN', '123456789'],
	['br_cnpj', 'CNPJ', '01.234.456/5432-10'],
	['br_cpf', 'CPF', '123.456.789-87'],
	['bs_tin', 'TIN', '123.456.789'],
	['by_tin', 'TIN', '123456789'],
	['ca_bn', 'BN', '123456789'],
	['ca_gst_hst', 'GST/HST number', '123456789RT0002'],
	['ca_pst_bc', 'PST number (British Columbia)', 'PST-1234-5678'],
	['ca_pst_mb', 'PST number (Manitoba)', '123456-7'],
	['ca_pst_sk', 'PST number (Saskatchewan)', '1234567'],
	['ca_qst', 'QST number (Québec)', '1234567890TQ1234'],
	['cd_nif', 'NIF', 'A0123456M'],
	['ch_uid', 'UID number', 'CHE-123.456.789 HR'],
	['ch_vat', 'VAT number', 'CHE-123.456.789 MWST'],
	['cl_tin', 'TIN', '12.345.678-K'],
	['cm_niu', 'NIU', 'M123456789000L'],
	['cn_tin', 'TIN', '123456789012345678'],
	['co_nit', 'NIT', '123.456.789-0'],
	['cr_tin', 'TIN', '1-234-567890'],
	['cv_nif', 'NIF', '213456789'],
	['de_stn', 'Steuernummer', '1234567890'],
	['do_rcn', 'RCN', '123-4567890-1'],
	['ec_ruc', 'RUC', '1234567890001'],
	['eg_tin', 'TIN', '123456789'],
	['es_cif', 'CIF', 'A12345678'],
	['et_tin', 'TIN', '1000123456'],
	// Out of wire-value order, deliberately and only here: the suggested group keeps table order,
	// and a buyer in the union is holding a VAT number. The One Stop Shop registration is a
	// *seller's*, so offering it first would put the wrong document under the cursor of almost
	// everybody who opens the list. `defaultTaxIDType` is pinned to this by its own test.
	['eu_vat', 'VAT number', 'DE123456789', EU_VAT_COUNTRIES, 'European Union'],
	['eu_oss_vat', 'One Stop Shop VAT number', 'EU123456789', EU_VAT_COUNTRIES, 'European Union'],
	['fo_vat', 'VAT number', '123456'],
	['gb_vat', 'VAT number', 'GB123456789'],
	['ge_vat', 'VAT number', '123456789'],
	['gi_tin', 'TIN', '12345678'],
	['gn_nif', 'NIF', '123456789'],
	['hk_br', 'BR number', '12345678'],
	['hr_oib', 'OIB', '12345678901'],
	['hu_tin', 'TIN', '12345678-1-23'],
	// Issued in Spain, and the only entry whose prefix is not a country at all: `IC` is the
	// reserved code for the Canary Islands, which a customer's address gives as `ES`.
	['ic_nif', 'NIF (Canary Islands)', 'A12345678', ['ES']],
	['id_npwp', 'NPWP', '012.345.678.9-012.345'],
	['il_vat', 'VAT number', '000012345'],
	['in_gst', 'GST number', '12ABCDE3456FGZH'],
	['is_vat', 'VAT number', '123456'],
	['it_cf', 'Codice fiscale', 'RSSMRA85T10A562S'],
	['jp_cn', 'Corporate number', '1234567891234'],
	['jp_rn', 'Registered foreign business number', '12345'],
	['jp_trn', 'Tax registration number', 'T1234567891234'],
	['ke_pin', 'PIN', 'P000111111A'],
	['kg_tin', 'TIN', '12345678901234'],
	['kh_tin', 'TIN', '1001-123456789'],
	['kr_brn', 'BRN', '123-45-67890'],
	['kz_bin', 'BIN', '123456789012'],
	['la_tin', 'TIN', '123456789-000'],
	['li_uid', 'UID number', 'CHE123456789'],
	['li_vat', 'VAT number', '12345'],
	['lk_vat', 'VAT number', '1234567890-7000'],
	['ma_vat', 'VAT number', '12345678'],
	['md_vat', 'VAT number', '1002000000001'],
	['me_pib', 'PIB', '12345678'],
	['mk_vat', 'VAT number', 'MK1234567890123'],
	['mr_nif', 'NIF', '12345678'],
	['mx_rfc', 'RFC', 'ABC010203AB9'],
	['my_frp', 'FRP number', '12345678'],
	['my_itn', 'ITN', 'C 1234567890'],
	['my_sst', 'SST number', 'A12-3456-78912345'],
	['ng_tin', 'TIN', '12345678-0001'],
	['no_vat', 'VAT number', '123456789MVA'],
	['no_voec', 'VOEC number', '1234567'],
	['np_pan', 'PAN', '123456789'],
	['nz_gst', 'GST number', '123456789'],
	['om_vat', 'VAT number', 'OM1234567890'],
	['pe_ruc', 'RUC', '12345678901'],
	['ph_tin', 'TIN', '123456789012'],
	['pl_nip', 'NIP', '1234567890'],
	['py_ruc', 'RUC', '1234567-8'],
	['ro_tin', 'TIN', '1234567890123'],
	['rs_pib', 'PIB', '123456789'],
	['ru_inn', 'INN', '1234567891'],
	['ru_kpp', 'KPP', '123456789'],
	['sa_vat', 'VAT number', '123456789012345'],
	['sg_gst', 'GST number', 'M12345678X'],
	['sg_uen', 'UEN', '123456789F'],
	['si_tin', 'TIN', '12345678'],
	['sn_ninea', 'NINEA', '12345672A2'],
	['sr_fin', 'FIN', '1234567890'],
	['sv_nit', 'NIT', '1234-567890-123-4'],
	['th_vat', 'VAT number', '1234567891234'],
	['tj_tin', 'TIN', '123456789'],
	['tr_tin', 'TIN', '0123456789'],
	['tw_vat', 'VAT number', '12345678'],
	['tz_vat', 'VAT number', '12345678A'],
	['ua_vat', 'VAT number', '123456789'],
	['ug_tin', 'TIN', '1014751879'],
	['us_ein', 'EIN', '12-3456789'],
	['uy_ruc', 'RUC', '123456789012'],
	['uz_tin', 'TIN', '123456789'],
	['uz_vat', 'VAT number', '123456789012'],
	['ve_rif', 'RIF', 'A-12345678-9'],
	['vn_tin', 'TIN', '1234567890'],
	['za_vat', 'VAT number', '4123456789'],
	['zm_tin', 'TIN', '1004751879'],
	['zw_tin', 'TIN', '1234567890']
];

/**
 * The country a wire value belongs to, read off its prefix.
 *
 * A prefix that is not an assigned alpha-2 code ties the type to no country rather than to a
 * made-up one — which is what keeps a future `xx_` scheme out of some unrelated country's list.
 */
function countriesFromPrefix(value: string): readonly string[] {
	const prefix = value.slice(0, 2).toUpperCase();
	return COUNTRY_CODES.includes(prefix) ? [prefix] : [];
}

/** Every tax ID type the store can send, in wire-value order. */
export const TAX_ID_TYPES: readonly TaxIDType[] = ROWS.map(([value, label, example, countries, region]) => ({
	value,
	label,
	example,
	countries: countries ?? countriesFromPrefix(value),
	...(region === undefined ? {} : { region })
}));

const BY_VALUE = new Map(TAX_ID_TYPES.map(type => [type.value, type]));

/** The type behind a stored wire value, or null for one this build does not know. */
export function taxIDType(value: string): TaxIDType | null {
	return BY_VALUE.get(value.trim()) ?? null;
}

/**
 * What a type is called on screen: the jurisdiction, then the document.
 *
 * Composed rather than stored, so the country half is the reader's own language and the document
 * half is the only part anybody has to maintain.
 */
export function taxIDTypeName(type: TaxIDType, locale?: string): string {
	const scope = type.region ?? (type.countries.length === 1 ? countryName(type.countries[0], locale) : '');
	return scope === '' ? type.label : `${scope} — ${type.label}`;
}

/**
 * The label for a value that may be one this build has never heard of.
 *
 * An unknown value renders as itself rather than as a blank: it is a real value the store is
 * holding and sending, and a picker that showed nothing where it sits would read as an empty field
 * somebody could save over without noticing.
 */
export function taxIDValueName(value: string, locale?: string): string {
	const type = taxIDType(value);
	return type === null ? value : taxIDTypeName(type, locale);
}

/** The specimen for a value, or the empty string where there is none to show. */
export function taxIDExample(value: string): string {
	return taxIDType(value)?.example ?? '';
}

export interface TaxIDTypeGroups {
	/** The documents issued where the customer says they are. Empty when no country is chosen. */
	suggested: readonly TaxIDType[];
	/** Everything else, in the reader's alphabetical order of jurisdiction. */
	others: readonly TaxIDType[];
}

/**
 * The list split into "where you are" and "everywhere else".
 *
 * The suggested half leads with the regional documents — an EU business almost always means its
 * VAT number rather than its national tax number — and the rest keeps wire-value order, which
 * groups a country's own documents together. The other half is sorted by the name the reader sees,
 * with `Intl.Collator` for the reason `countryOptions` gives.
 */
export function taxIDTypeGroups(country: string, locale?: string): TaxIDTypeGroups {
	const code = country.trim().toUpperCase();
	const matches = (type: TaxIDType): boolean => code !== '' && type.countries.includes(code);

	const suggested = TAX_ID_TYPES.filter(matches).sort((left, right) => {
		const leftRegional = left.countries.length > 1 ? 0 : 1;
		const rightRegional = right.countries.length > 1 ? 0 : 1;
		return leftRegional - rightRegional;
	});

	const resolved = locale ?? (typeof navigator === 'undefined' ? 'en' : navigator.language);
	const collator = new Intl.Collator(resolved);
	const others = TAX_ID_TYPES.filter(type => !matches(type)).sort((left, right) =>
		collator.compare(taxIDTypeName(left, resolved), taxIDTypeName(right, resolved))
	);

	return { suggested, others };
}

/**
 * What a fresh row should start on: the likeliest document for the country on file.
 *
 * A blank row on a form with a hundred and eighteen options is a question with no starting point,
 * and the answer is the same for almost everybody who adds one — so it is offered, pre-chosen and
 * changeable, rather than demanded. No country means no guess: an unprompted `ad_nrt` at the top
 * of the alphabet is worse than an empty field, because it is a wrong answer that reads as a
 * chosen one.
 */
export function defaultTaxIDType(country: string): string {
	return taxIDTypeGroups(country).suggested[0]?.value ?? '';
}

/**
 * The document a number says it is, read off the number itself.
 *
 * Almost every tax identifier worth picking a type for **names its own jurisdiction in its first
 * two characters** — `DE123456789`, `GB123456789`, `CHE-123.456.789`, `ATU12345678` — and that is
 * the half the customer already has in their hand. Asking them to classify it first, out of a
 * hundred and eighteen wire values, is asking the question backwards: they know their VAT number,
 * they do not know that it is spelled `eu_vat`.
 *
 * So the number leads and this reads the type off it. What comes back is the **same answer
 * `defaultTaxIDType` would give for that country**, rather than a second ranking that could
 * disagree with the picker sitting beside it — an EU member resolves to the EU VAT number, not to
 * its national one, because that is what a number starting `DE` is.
 *
 * Three limits, and each is deliberate. A prefix that is not a country code answers **nothing**
 * rather than guessing: an Australian ABN and a Brazilian CNPJ are digits throughout, and there is
 * no honest inference to make from them. Two letters **alone** answer nothing either, since `DE` is
 * somebody halfway through typing rather than a number. And `EU`, which is not an ISO code, is the
 * one prefix named outright — a One Stop Shop number genuinely starts with it, and it is the only
 * case where the prefix is a scheme rather than a country.
 *
 * Nothing here validates: the provider is the authority on format, and a wrong guess costs a
 * changed dropdown rather than a refused save. That is also why the caller only ever applies this
 * to a type the customer has not chosen for themselves.
 */
export function inferTaxIDType(value: string): string {
	const cleaned = value.replace(/[\s.\-/]/g, '').toUpperCase();
	if (cleaned.length < 3) return '';

	const prefix = cleaned.slice(0, 2);
	if (!/^[A-Z]{2}$/.test(prefix)) return '';
	if (prefix === 'EU') return 'eu_oss_vat';

	return defaultTaxIDType(prefix);
}
